import { useLocation } from "react-router-dom";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLazyQuery, useQuery } from "@apollo/client";
import { isLoginCheckQuery } from "../query/user.query";
import { useSelector } from "react-redux";
import { themeQuery } from "../query/theme.query";
import { resetReportFilters } from "@src/middleware/redux/reducers/reportFilters.reducer";
import { socket, connectSocket } from '../../config/socket';
import { performFullLogout } from '../logout.util';


async function subscribe() {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: Uint8Array.from(
            atob("BNdYP56lkxXX5QK5-wUupFLa7gFnHwCUs4vNUqzHEq22K6Yiss044K6OM8Nllna4ROJsgb8V-dbZwL2UWTouij0".replace(/_/g,"/").replace(/-/g,"+")),
            c => c.charCodeAt(0)
        )
    });
    return sub;
}

async function enableNotifications() {
    const permission = Notification.permission;
    if (permission === "granted") return await subscribe();
    if (permission === "denied") return;
    const newPermission = await Notification.requestPermission();
    if (newPermission === "granted") return await subscribe();
}


export const PrivateRoutes = ({children})=>{

    // Support session: a WFC admin is viewing this tenant's portal via a
    // temporary session token. Skip all regular auth checks — the token is
    // verified by the server on every GraphQL request.
    const isSupportSession = !!sessionStorage.getItem('supportSessionToken');

    // Guard renders as null until we confirm a session exists locally.
    // useLayoutEffect fires synchronously before the browser paints, so
    // protected UI is never visible to an unauthenticated user — not even
    // for one frame.
    const [authReady, setAuthReady] = useState(isSupportSession);

    useLayoutEffect(()=>{
        if (isSupportSession) return;
        if (!localStorage.getItem('deviceId')) {
            // No session at all — hard redirect, show nothing
            performFullLogout();
            return;
        }
        setAuthReady(true);
    }, []);

    // Server-side session check: if the session was remotely terminated the
    // authChecker will reject and the Apollo error link calls performFullLogout().
    // We do a proactive check here on mount as an extra safety net.
    const [checkSession, {error: sessionError}] = useLazyQuery(isLoginCheckQuery, {
        variables: { deviceId: localStorage.getItem('deviceId') },
        fetchPolicy: 'network-only',
    });

    useEffect(()=>{
        if (authReady && !isSupportSession) checkSession();
    }, [authReady]);

    useEffect(()=>{
        if (sessionError?.message?.length > 0 && !isSupportSession) {
            performFullLogout();
        }
    }, [sessionError]);

    const {authenticatedUserDetail} = useSelector(state=>state.userAuthReducer);

    useEffect(()=>{
        if(authenticatedUserDetail?._id){
            connectSocket();
            enableNotifications().then((res)=>{
                socket.emit('userJoin', { userId: authenticatedUserDetail?._id, subToken: res});
            });
        }
    },[authenticatedUserDetail?._id]);

    const dispatch = useDispatch();

    const {data:themeData, loading:themeLoading, refetch: refetchTheme} = useQuery(themeQuery,{
        variables:{
            userId: authenticatedUserDetail?._id
        },
        skip: !authenticatedUserDetail?._id || !authReady,
        fetchPolicy: 'cache-and-network'
    });

    const renderChildrenWithProps = () => {
        return React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, { themeData, themeLoading, refetchTheme });
            }
            return child;
        });
    };

    const loc = useLocation();
    useEffect(()=>{
        if(!loc?.pathname?.includes("/user/sysreport")){
            dispatch(resetReportFilters());
            sessionStorage.setItem('sysreportReturnPath', loc?.pathname);
        }
    },[loc]);

    // Render nothing until local auth check passes — no protected UI ever leaks
    if (!authReady) return null;

    return (
        <>
            {renderChildrenWithProps()}
        </>
    );
}
